import React, { EventHandler, useEffect, useState } from 'react'
import { serverError } from 'remix-utils'
import type { ZodError } from 'zod'
import { z } from 'zod'
import { zx } from 'zodix'

import type { ActionArgs, LoaderArgs, V2_MetaFunction } from '@remix-run/node'
import { json, redirect } from '@remix-run/node'
import {
  Form,
  Link,
  useActionData,
  useFetcher,
  useLoaderData,
  useNavigation,
  useSearchParams,
} from '@remix-run/react'

import LoaderSpinner from '~/components/LoaderSpinner'

import Dropdown from '~/components/Dropdown'
import Recaptcha from '~/components/Recaptcha'

import { createUserSession, getUserId } from '~/models/session.server'
import {
  checkUserExistsByEmail,
  createUser,
  signupUserInput,
} from '~/models/user.server'
import { rateLimit } from '~/utils/rate-limit.server'

import categoriesList from '../static/categories.json'

const checkDisposableEmail = async (email: string) => {
  try {
    const activeEnv = (process.env.APP_ENV || process.env.NODE_ENV || '').toLowerCase()
    const skipDisposableCheck = ['local', 'development'].includes(activeEnv)

    // Skip check in local/development environments
    if (skipDisposableCheck) {
      return false
    }

    const response = await fetch(`https://disposable.debounce.io/?email=${email}`)
    const disposableEmailData = (await response.json()) as { disposable: string }
    if (disposableEmailData && disposableEmailData.disposable === 'true') {
      return true
    }
    return false
  } catch (error) {
    return false
  }
}

export const meta: V2_MetaFunction = () => [{ title: 'Sign Up' }]

export async function loader({ request }: LoaderArgs) {
  try {
    const { category } = zx.parseQuery(request, {
      category: z.string().optional(),
    })
    const userId = await getUserId(request)
    if (userId) return redirect('/')

    const categories = categoriesList.map((c) => c.Name)
    const subcategories = category
      ? categoriesList
        .find((c) => c.Name === category)
        ?.Children.map((c) => c.Name)
      : categoriesList[0].Children.map((c) => c.Name)

    const siteKey = process.env.RECAPTCHA_SITE_KEY

    return json({ categories, subcategories, siteKey })
  } catch (err) {
    return json({ categories: [], subcategories: [], siteKey: undefined })
  }
}

export async function action({ request }: ActionArgs) {
  try {
    const rateLimitResult = rateLimit(request)
    if (!rateLimitResult.success) {
      return json(
        {
          errors: [
            {
              message: 'Too many signup attempts. Please try again in an hour.',
              code: 'custom',
              path: ['rateLimit'],
            },
          ] as ZodError['issues'],
        },
        { status: 429 }
      )
    }

    const formData = await request.clone().formData()
    const honeypot = formData.get('company_email')

    // If honeypot field is filled, it's likely a bot. Redirect to "/"
    if (honeypot) {
      console.log('Honeypot field filled, redirecting to home.')
      return redirect('/')
    }

    const recaptchaToken = formData.get('g-recaptcha-response')
    const secretKey = process.env.RECAPTCHA_SECRET_KEY

    if (!recaptchaToken) {
      return json(
        {
          errors: [
            {
              message: 'Please complete the reCAPTCHA',
              code: 'custom',
              path: ['captcha'],
            },
          ] as ZodError['issues'],
        },
        { status: 400 }
      )
    }

    // Verify reCAPTCHA Token
    const verifyResponse = await fetch(
      'https://www.google.com/recaptcha/api/siteverify',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `secret=${secretKey}&response=${recaptchaToken}`,
      }
    )

    const verifyData = (await verifyResponse.json()) as { success: boolean }
    if (!verifyData.success) {
      return json(
        {
          errors: [
            {
              message: 'reCAPTCHA verification failed, please try again',
              code: 'custom',
              path: ['captcha'],
            },
          ] as ZodError['issues'],
        },
        { status: 400 }
      )
    }

    const userInput = await zx.parseFormSafe(request, signupUserInput)
    if (!userInput.success) {
      return json({ errors: userInput.error.issues }, { status: 400 })
    }

    const { email, redirectTo } = userInput.data

    const isDisposable = await checkDisposableEmail(email)
    if (isDisposable) {
      return json(
        {
          errors: [
            {
              message: 'Disposable emails are not allowed, please use a permanent email address.',
              code: 'custom',
              path: ['email'],
            },
          ] as ZodError['issues'],
        },
        { status: 400 }
      )
    }

    const existingUser = await checkUserExistsByEmail(email)
    if (existingUser) {
      return json(
        {
          errors: [
            {
              message: 'User already exists, perhaps try to log in?',
              code: 'custom',
              path: ['existingUser'],
            },
          ] as ZodError['issues'],
        },
        { status: 400 }
      )
    }
    const user = await createUser(userInput.data)

    if (!user) {
      throw serverError('Unable to create new user, please try again')
    }
    return redirect(`/signup/verify?email=${encodeURIComponent(email)}&redirectTo=${encodeURIComponent(redirectTo)}`)
  } catch (err: any) {
    console.log('\n singup action error:', err.message || err);
    let message = err.message || 'Something went wrong'
    return json(
      {
        errors: [
          {
            message: message,
            code: 'custom',
          },
        ] as ZodError['issues'],
      },
      { status: 400 }
    )
  }
}

export default function Join() {
  const { categories, subcategories, siteKey } = useLoaderData<typeof loader>()
  const [searchParams] = useSearchParams()
  const navigation = useNavigation()
  const redirectTo = searchParams.get('redirectTo') ?? '/campaigns'
  const actionData = useActionData<typeof action>()
  const emailRef = React.useRef<HTMLInputElement>(null)
  const passwordRef = React.useRef<HTMLInputElement>(null)
  const confirmPasswordRef = React.useRef<HTMLInputElement>(null)

  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false)

  const captchaError = actionData?.errors.find((e: any) =>
    e?.path?.includes('captcha')
  )
  const existingUserError = actionData?.errors.find((e: any) =>
    e?.path?.includes('existingUser')
  )
  const rateLimitError = actionData?.errors.find((e: any) =>
    e?.path?.includes('rateLimit')
  )
  const emailError = actionData?.errors.find((e: any) => e?.path?.includes('email'))
  const passwordError = actionData?.errors.find((e: any) =>
    e?.path?.includes('password')
  )
  const confirmPasswordError = actionData?.errors.find((e: any) =>
    e?.path?.includes('confirmPassword')
  )

  useEffect(() => {
    if (emailError || existingUserError || rateLimitError) {
      emailRef.current?.focus()
    } else if (passwordError) {
      passwordRef.current?.focus()
    } else if (confirmPasswordError) {
      confirmPasswordRef.current?.focus()
    }
  }, [existingUserError, emailError, passwordError, confirmPasswordError, rateLimitError])

  const subcategoryFetcher = useFetcher()
  function fetchSubcategories(e: React.ChangeEvent<HTMLSelectElement>) {
    subcategoryFetcher.load(
      `/signup?category=${encodeURIComponent(e.target.value)}`
    )
  }

  useEffect(() => {
    console.log('\n action data...', actionData)
  }, [actionData])

  const isSubmitting = navigation.state === 'submitting'

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <main>
      <section className="mt-24 overflow-hidden bg-white xl:pb-56">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading tracking-px-n mb-[45px] text-center text-6xl font-bold leading-tight text-primary md:text-7xl">
              Sign Up
            </h2>
            <p className="mb-3">
              Before we can get started, we just need a few more details.
            </p>
            <Form method="post">
              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Your Name</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  id="name"
                  required
                  autoFocus={true}
                  name="name"
                  type="text"
                  placeholder="First &amp; Last Name"
                />
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Email</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  ref={emailRef}
                  id="email"
                  required
                  name="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={
                    emailError || existingUserError || rateLimitError ? true : undefined
                  }
                  aria-describedby="email-error"
                  placeholder="Email Address"
                />
                {emailError && (
                  <div className="pt-1 text-red-700" id="email-error">
                    {emailError.message}
                  </div>
                )}
                {existingUserError && (
                  <div className="pt-1 text-red-700" id="email-error">
                    {existingUserError.message}
                  </div>
                )}
                {rateLimitError && (
                  <div className="pt-1 font-bold text-red-700" id="email-error">
                    {rateLimitError.message}
                  </div>
                )}
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">
                    Company Name
                  </p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="company"
                  type="text"
                  required
                  placeholder="Company Name"
                />
                <p className="m-1 text-xs text-gray-500">
                  This will be the name of your default advertiser
                </p>
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">
                    Manager Name
                  </p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="manager_name"
                  type="text"
                  placeholder="Manager Name"
                />
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">
                    Manager Email
                  </p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="manager_email"
                  type="email"
                  placeholder="Manager Email"
                />
              </label>
              {/* <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Website</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="websiteUrl"
                  type="url"
                  placeholder="Website Url"
                  required
                />
              </label>

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">Logo URL</p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="logoUrl"
                  type="text"
                  placeholder="Logo Url"
                  required
                />
                <p className="m-1 text-xs text-gray-500">
                  Link to a png or jpeg of your logo
                </p>
              </label>

              <Dropdown
                label="Category"
                name="category"
                options={categories}
                required={true}
                onChange={fetchSubcategories}
              />

              <Dropdown
                label="Subcategory"
                name="subcategory"
                options={
                  subcategoryFetcher.data?.subcategories ?? subcategories
                }
                required={true}
              /> */}

              <label className="mb-5 block text-left">
                <div className="flex space-x-2">
                  <p className="pb-2 font-medium text-[#525252]">
                    Company Address
                  </p>
                  <p className="font-bold text-primary">*</p>
                </div>
                <input
                  className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                  name="addressStreet"
                  type="text"
                  placeholder="Street"
                  required
                />
                <div className="flex  w-full space-x-2 pt-3.5">
                  <input
                    className=" w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700 md:w-2/3"
                    name="addressCity"
                    type="text"
                    placeholder="City"
                    required
                  />
                  <input
                    className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700 md:w-1/3"
                    name="addressPostcode"
                    type="text"
                    placeholder="Postcode"
                    required
                    maxLength={10}
                    minLength={4}
                  />
                </div>
              </label>

              {/* <div className="flex w-full space-x-0 md:space-x-2 flex-wrap md:flex-nowrap">
                <label className="mb-5 block w-full text-left">
                  <div className="flex space-x-2">
                    <p className="pb-2 font-medium text-[#525252]">Password</p>
                    <p className="font-bold text-primary">*</p>
                  </div>
                  <input
                    className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                    id="password"
                    ref={passwordRef}
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={passwordError ? true : undefined}
                    aria-describedby="password-error"
                    placeholder="Password"
                    required
                  />
                  {passwordError && (
                    <div className="pt-1 text-red-700" id="password-error">
                      {passwordError?.message}
                    </div>
                  )}
                </label>

                <label className="mb-5 block w-full text-left">
                  <div className="flex space-x-2">
                    <p className="pb-2 font-medium text-[#525252]">
                      Confirm Password
                    </p>
                    <p className="font-bold text-primary">*</p>
                  </div>
                  <input
                    className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700"
                    id="confirmPassword"
                    ref={confirmPasswordRef}
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm Password"
                    required
                  />
                  {confirmPasswordError && (
                    <div className="pt-1 text-red-700" id="password-error">
                      {confirmPasswordError?.message}
                    </div>
                  )}
                </label>
              </div> */}

              <div className="flex w-full space-x-0 md:space-x-2 flex-wrap md:flex-nowrap">
                <label className="mb-5 block w-full text-left">
                  <div className="flex space-x-2">
                    <p className="pb-2 font-medium text-[#525252]">Password</p>
                    <p className="font-bold text-primary">*</p>
                  </div>
                  <div className="relative">
                    <input
                      className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700 pr-10"
                      id="password"
                      ref={passwordRef}
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      aria-invalid={passwordError ? true : undefined}
                      aria-describedby="password-error"
                      placeholder="Password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <div className="pt-1 text-red-700" id="password-error">
                      {passwordError?.message}
                    </div>
                  )}
                </label>

                <label className="mb-5 block w-full text-left">
                  <div className="flex space-x-2">
                    <p className="pb-2 font-medium text-[#525252]">Confirm Password</p>
                    <p className="font-bold text-primary">*</p>
                  </div>
                  <div className="relative">
                    <input
                      className="w-full rounded-lg border border-primary bg-white px-4 py-3.5 font-medium text-gray-500 placeholder-gray-500 outline-none focus:ring focus:ring-green-700 pr-10"
                      id="confirmPassword"
                      ref={confirmPasswordRef}
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm Password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {confirmPasswordError && (
                    <div className="pt-1 text-red-700" id="confirm-password-error">
                      {confirmPasswordError?.message}
                    </div>
                  )}
                </label>
              </div>

              {/* Honeypot Field (Invisible Trap) */}
              <div style={{ display: 'none' }} aria-hidden="true">
                <label htmlFor="company_email">Company Email</label>
                <input
                  id="company_email"
                  name="company_email"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <input type="hidden" name="redirectTo" value={redirectTo} />

              {siteKey && (
                <div className="mb-5 flex flex-col items-left">
                  <Recaptcha
                    siteKey={siteKey}
                    onVerify={React.useCallback((token: string | null) => setIsCaptchaVerified(!!token), [])}
                  />
                  {captchaError && (

                    <div className="pt-1 text-red-700">{captchaError.message}</div>
                  )}
                </div>
              )}

              <button
                className={`hover:shadow-4xl flex items-center justify-center mb-8 w-full rounded-xl border py-4 px-9 font-semibold text-white shadow-sm transition duration-200 ease-in-out focus:ring focus:ring-indigo-300 ${
                  isCaptchaVerified && !isSubmitting
                    ? 'bg-secondary hover:bg-lightBlue'
                    : 'cursor-not-allowed bg-gray-400 opacity-50'
                }`}
                type="submit"
                disabled={!isCaptchaVerified || isSubmitting}
              >
                {isSubmitting ? <LoaderSpinner /> : 'Sign Up'}
              </button>
              {/* <p className="space-x-2 font-medium">
                <span>Already have an account?</span>
                <Link className="text-primary hover:text-green-700" to="/login">
                  Login
                </Link>
              </p> */}
            </Form>
          </div>
        </div>
      </section>
    </main>
  )
}
